import type { GridBasicGroupNode, GridRowId, GridTreeNode } from "@mui/x-data-grid-premium";
import equal from "fast-deep-equal";
import type { ReportCell, ReportColumn, ReportGroupTotal } from "../../../api/types/widgetTypes";

export const grandTotalsRowId = "grand-totals-row";

interface GroupTotalValueApi {
  getRowNode: (id: GridRowId) => GridTreeNode | null;
}

const getGroupKeys = (rowNode: GridBasicGroupNode, api: GroupTotalValueApi, columns: ReportColumn[]) => {
  const filters: Record<string, Partial<ReportCell>> = {};
  if (rowNode.groupingKey !== null) {
    const field = columns.find((f) => f.id === rowNode.groupingField);
    if (!field) {
      return {};
    }

    filters[field.name] = {
      formattedValue: rowNode.groupingKey.toString(),
      valueType: "String",
    };
  }

  if (rowNode.parent !== null) {
    const parentNode = api.getRowNode(rowNode.parent);
    if (parentNode !== null && parentNode.type === "group") {
      const parentKeys = getGroupKeys(parentNode, api, columns);
      Object.assign(filters, parentKeys);
    }
  }

  return filters;
};

const hasSameFilters = (filters: Record<string, ReportCell>, groupKeys: Record<string, Partial<ReportCell>>) => {
  if (filters === groupKeys) {
    return true;
  }

  if (filters === null || groupKeys === null) {
    return false;
  }

  if (Object.keys(filters).length !== Object.keys(groupKeys).length) {
    return false;
  }

  for (const key in groupKeys) {
    if (!equal(filters[key]?.formattedValue, groupKeys[key]?.formattedValue)) {
      return false;
    }
  }

  return true;
};

export const getGroupTotalValue = (
  groupRowId: GridRowId,
  columnId: string,
  columns: ReportColumn[],
  groupTotals: ReportGroupTotal[],
  api: GroupTotalValueApi
): string | undefined => {
  const groupNode = api.getRowNode(groupRowId);

  if (groupNode !== null && groupNode.type === "group") {
    const filters = getGroupKeys(groupNode, api, columns);
    if (filters === undefined) {
      return undefined;
    }

    const group = groupTotals.find((gt) => hasSameFilters(gt.filters, filters));
    if (group !== undefined) {
      const cell = group.values[columnId];
      return cell?.formattedValue;
    }
  }

  return undefined;
};

export const getReportColumnAlignment = (column: ReportColumn): "left" | "right" | "center" => {
  if (["Numeric", "Date", "DateTime"].includes(column.dataType)) {
    return "right";
  }

  if (column.dataType === "Boolean") {
    return "center";
  }

  return "left";
};
